import groupBy from 'lodash/groupBy';
import { ILine } from '../../../../../models/line.model';
import { TransportMode } from '../../../../../models/transport-mode.enum';
import Fact from '../../../../Shared/Fact';
import { TrafficSituationHelper } from '../../../../../helpers/traffic-situation.helper';
import { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import LineDescription from '../../../../Shared/LineDescription';
import { TransportModeHelper } from '../../../../../helpers/transport-mode.helper';

// MAIN COMPONENT
/**
 * LineListFact
 * Displays affected lines for a traffic situation, grouped by transport mode.
 *
 * Behavior
 * - Uses TrafficSituationHelper.getDistinctLines to deduplicate lines.
 * - TransportModeLines Groups lines by TransportMode; renders either:
 *   - A vertical list with direction names (for transport modes in showWithDirectionName).
 *   - A horizontal list of line badges (for all other transport modes).
 * - Each line is rendered via LineDescription (badge + optional details).
 * - If there is only one transport mode present, renders a single group without a surrounding list.
 * - Returns null when there are no lines.
 *
 * Remarks
 * - This component replaces the use of LineListShowcase with multiple lines.
 *
 * Props
 * - trafficSituation: ITrafficSituation to extract and group lines from.
 * - useShortName?: Whether to use the short line name. Defaults to false.
 * - showWithDirectionName?: Transport modes that render with direction names. Defaults to [TransportMode.train].
 * - className?: Optional classes applied to the top-level wrapper.
 */
export default function LineListFact({
  trafficSituation,
  useShortName = false,
  showWithDirectionName = [TransportMode.train],
  className = '',
}: {
  trafficSituation: ITrafficSituation;
  useShortName?: boolean;
  showWithDirectionName?: TransportMode[];
  className?: string;
}): React.ReactNode | null {
  const distinctLines = TrafficSituationHelper.getDistinctLines(trafficSituation);

  if (distinctLines.length === 0) return null;

  const linesByTransportMode = groupBy(distinctLines, (line) => line.transportMode);

  function getTransportModeComponent(transportMode: TransportMode, transportModeLines: ILine[]): React.ReactNode {
    return <TransportModeLines transportMode={transportMode} lines={transportModeLines} useShortName={useShortName} showDirectionName={showWithDirectionName.includes(transportMode)} />;
  }

  if (Object.keys(linesByTransportMode).length === 1)
    return <div className={className}>{getTransportModeComponent(Object.keys(linesByTransportMode)[0] as TransportMode, Object.values(linesByTransportMode)[0])}</div>;

  return (
    <ul className={`list-unstyled d-flex flex-column gap-2 ${className ?? ''}`.trimEnd()}>
      {Object.entries(linesByTransportMode).map(([transportMode, transportModeLines]) => (
        <li key={transportMode} className="">
          {getTransportModeComponent(transportMode as TransportMode, transportModeLines)}
        </li>
      ))}
    </ul>
  );
}

// HELPER COMPONENTS
function TransportModeLines({
  transportMode,
  lines,
  useShortName,
  showDirectionName,
}: {
  transportMode: TransportMode;
  lines: ILine[];
  useShortName: boolean;
  showDirectionName: boolean;
}): React.ReactNode {
  const icon = TransportModeHelper.getIcon(transportMode);
  const label = TransportModeHelper.translate(transportMode);
  return (
    <Fact iconMotif={icon.motif} iconVariant={icon.variant} iconColor={icon.color} altText={label ? '' : undefined}>
      <div className={`d-flex ${showDirectionName ? 'flex-column align-items-start' : 'flex-wrap align-items-center'} gap-1`}>
        {label && <span className="fw-bold lh-sm">{label}:</span>}
        <ul className={`list-unstyled d-flex ${showDirectionName ? 'flex-column' : 'flex-row flex-wrap'} gap-1 mb-0`}>
          {lines.map((line) => (
            <li key={line.gid}>
              <LineDescription line={line} showTransportMode={false} showDirectionName={showDirectionName} useShortName={useShortName} />
            </li>
          ))}
        </ul>
      </div>
    </Fact>
  );
}
